import { useState, useEffect } from "react";
import { motion } from "motion/react";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";

interface ResultsData {
  client: number;
  jobs: number;
  users: number;
}

export default function ProvenResults() {
  const [resultsData, setResultsData] = useState<ResultsData>({
    client: 50, // Default fallback values
    jobs: 10,
    users: 30
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Subscribe to real-time updates from Firestore
    const unsubscribe = onSnapshot(
      doc(db, "results_landing", "public"),
      (doc) => {
        if (doc.exists()) {
          const data = doc.data() as ResultsData;
          setResultsData({
            client: data.client || 50,
            jobs: data.jobs || 10,
            users: data.users || 30
          });
        }
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching results data:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);
  return (
    <section className="relative pt-12 min-[375px]:pt-14 min-[425px]:pt-16 sm:pt-20 pb-32 min-[375px]:pb-36 min-[425px]:pb-40 sm:pb-48 md:pb-56 lg:pb-64" style={{ background: 'linear-gradient(246deg, #594FE0 3.8%, #4175E6 59.93%, #34C7FA 101.7%)' }}>
      <motion.div
        className="mb-6 min-[375px]:mb-8 min-[425px]:mb-10 sm:mb-12 lg:mb-16 text-center"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      >
        <h2 className="text-white text-xl min-[375px]:text-2xl min-[425px]:text-3xl sm:text-4xl md:text-5xl lg:text-[57px] font-bold uppercase space-grotesk leading-[120%] sm:leading-[130%] mb-4 min-[375px]:mb-6 min-[425px]:mb-8 sm:mb-8 border-b border-white/50 pb-4 min-[375px]:pb-6 min-[425px]:pb-8 sm:pb-[30px] text-center md:text-left md:ml-4 min-[375px]:md:ml-6 lg:ml-12 xl:ml-20 px-4 sm:px-0">
          Live Results
        </h2>
      </motion.div>
      <div className="flex flex-col justify-center max-w-7xl mx-auto px-4 min-[375px]:px-6 min-[425px]:px-8 sm:px-6 lg:px-8">

        {/* Stats Cards */}
        <motion.div
          className="grid grid-cols-1 min-[425px]:grid-cols-2 lg:grid-cols-3 gap-3 min-[375px]:gap-4 min-[425px]:gap-6 lg:gap-8"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.18 } } }}
        >
          {/* Stat 1 - Clients Onboarded */}
          <motion.div
            className="flex flex-col items-center text-center h-full min-h-[140px] min-[375px]:min-h-[160px] min-[425px]:min-h-[180px] lg:min-h-[200px] p-3 min-[375px]:p-4 min-[425px]:p-6 lg:p-8 rounded-[10px] transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-white/20 cursor-pointer"
            style={{ background: 'linear-gradient(193deg, rgba(255, 255, 255, 0.00) 36.59%, rgba(255, 255, 255, 0.60) 216.11%)' }}
            variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } } }}
          >
            {loading ? (
              <div className="animate-pulse">
                <div className="h-12 w-16 bg-white/30 rounded mb-3"></div>
                <div className="h-4 w-24 bg-white/20 rounded"></div>
              </div>
            ) : (
              <>
                <span className="text-white text-2xl min-[375px]:text-3xl min-[425px]:text-4xl md:text-5xl lg:text-6xl font-bold uppercase space-grotesk leading-[130%] mb-1 min-[375px]:mb-2 min-[425px]:mb-3">
                  {resultsData.client}
                </span>
                <span className="text-white/90 text-xs min-[375px]:text-sm min-[425px]:text-base lg:text-lg manrope leading-[140%]">
                  Clients onboarded
                </span>
              </>
            )}
          </motion.div>

          {/* Stat 2 - Live Requirements */}
          <motion.div
            className="flex flex-col items-center text-center h-full min-h-[140px] min-[375px]:min-h-[160px] min-[425px]:min-h-[180px] lg:min-h-[200px] p-3 min-[375px]:p-4 min-[425px]:p-6 lg:p-8 rounded-[10px] transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-white/20 cursor-pointer"
            style={{ background: 'linear-gradient(193deg, rgba(255, 255, 255, 0.00) 36.59%, rgba(255, 255, 255, 0.60) 216.11%)' }}
            variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } } }}
          >
            {loading ? (
              <div className="animate-pulse">
                <div className="h-12 w-16 bg-white/30 rounded mb-3"></div>
                <div className="h-4 w-24 bg-white/20 rounded"></div>
              </div>
            ) : (
              <>
                <span className="text-white text-2xl min-[375px]:text-3xl min-[425px]:text-4xl md:text-5xl lg:text-6xl font-bold uppercase space-grotesk leading-[130%] mb-1 min-[375px]:mb-2 min-[425px]:mb-3">
                  {resultsData.jobs}
                </span>
                <span className="text-white/90 text-xs min-[375px]:text-sm min-[425px]:text-base lg:text-lg manrope leading-[140%]">
                  Live requirements
                </span>
              </>
            )}
          </motion.div>

          {/* Stat 3 - Active Referrers */}
          <motion.div
            className="flex flex-col items-center text-center h-full min-h-[140px] min-[375px]:min-h-[160px] min-[425px]:min-h-[180px] lg:min-h-[200px] p-3 min-[375px]:p-4 min-[425px]:p-6 lg:p-8 rounded-[10px] min-[425px]:col-span-2 lg:col-span-1 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-white/20 cursor-pointer"
            style={{ background: 'linear-gradient(193deg, rgba(255, 255, 255, 0.00) 36.59%, rgba(255, 255, 255, 0.60) 216.11%)' }}
            variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } } }}
          >
            {loading ? (
              <div className="animate-pulse">
                <div className="h-12 w-16 bg-white/30 rounded mb-3"></div>
                <div className="h-4 w-24 bg-white/20 rounded"></div>
              </div>
            ) : (
              <>
                <span className="text-white text-2xl min-[375px]:text-3xl min-[425px]:text-4xl md:text-5xl lg:text-6xl font-bold uppercase space-grotesk leading-[130%] mb-1 min-[375px]:mb-2 min-[425px]:mb-3">
                  {resultsData.users}
                </span>
                <span className="text-white/90 text-xs min-[375px]:text-sm min-[425px]:text-base lg:text-lg manrope leading-[140%]">
                  Active Referrers
                </span>
              </>
            )}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
